import React, { useEffect, useMemo, useState } from "react";
import he from "he";
import { ChevronDown, ChevronUp, Languages, Mic, Square, Turtle, Volume2 } from "lucide-react";
import { usePhonemeRecognition } from "@/speechToText/usePhonemeRecognition";
import TranslateService from "@/services/TranslateService";
import PracticeSessionStatsService from "@/services/PracticeSessionStatService";
import PhonemeInstructorService from "@/services/PhonemeInstructorService";
import { speakText, stopSpeaking } from "@/utils/speechPlayer";
import { accuracyToColor } from "@/lib/theme";
import { cn } from "@/lib/utils";
import PriorityBadge from "@/components/LearningSession/PriorityBadge";
import useStore from "@/store";

const successSound = typeof Audio !== "undefined" ? new Audio("/correct.mp3") : null;

// Phoneme entries arrive as {symbol: status} objects (or bare strings).
const entryOf = (item) => {
  if (typeof item === "string") return [item, ""];
  if (item && typeof item === "object") return Object.entries(item)[0] || ["", ""];
  return ["", ""];
};

// One tile per target sound: what it should be, whether it was right, and
// what the learner said instead.
const tilesFromSegment = (phonemeMatches, transcribedPhonemes, phonemes, soundRisks) => {
  const reference = (phonemeMatches?.length ? phonemeMatches : phonemes) || [];
  return reference
    .map((item, index) => {
      const [symbol, status] = entryOf(item);
      const [said] = entryOf(transcribedPhonemes?.[index]);
      return {
        symbol,
        said,
        correct: status ? status.toUpperCase() === "M" : null,
        risk: soundRisks?.[index] || null,
      };
    })
    .filter((tile) => tile.symbol);
};

const tilesFromMatches = (directMatches) =>
  (directMatches || [])
    .filter((match) => match?.original_phoneme)
    .map((match) => ({
      symbol: match.original_phoneme,
      said: match.transcribed_phoneme || "",
      correct: Boolean(match.is_correct),
      risk: match.risk || null,
      name: match.phoneme_name && match.phoneme_name !== "Unknown"
        ? match.phoneme_name.replace(/_/g, " ")
        : "",
    }));

const ScoreBadge = ({ value }) => {
  const color = accuracyToColor(value);
  return (
    <div
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-display text-sm font-bold"
      style={{
        color,
        background: `conic-gradient(${color} ${value * 3.6}deg, rgb(var(--line)) 0deg)`,
      }}
      aria-label={`Score ${Math.round(value)} percent`}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-elevated">
        {Math.round(value)}
      </span>
    </div>
  );
};

// One row of the sound table: the target sound, a play button, and either
// "Excellent!" or what the learner said plus an on-demand hint.
const SoundRow = ({ tile, language, nativeLanguage }) => {
  const [hint, setHint] = useState(null);
  const [hintOpen, setHintOpen] = useState(false);
  const [hintLoading, setHintLoading] = useState(false);
  const [hintError, setHintError] = useState(false);

  const toggleHint = async () => {
    if (hintOpen) {
      setHintOpen(false);
      return;
    }
    setHintOpen(true);
    if (hint || hintLoading) return;
    setHintLoading(true);
    setHintError(false);
    try {
      const response = await PhonemeInstructorService.getPhonemeInstructions(
        tile.symbol,
        tile.said,
        (language || "en").split("-")[0],
        nativeLanguage
      );
      const steps = (response?.instructions || "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
      setHint(steps);
    } catch (error) {
      setHintError(true);
    } finally {
      setHintLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-[88px_1fr] items-start gap-y-2 px-3 py-2.5">
      <div className="flex items-center gap-2">
        <span
          className="min-w-[28px] font-mono text-lg leading-7 text-fg"
          title={tile.name || undefined}
        >
          {tile.symbol}
        </span>
        <button
          type="button"
          onClick={() => speakText(tile.symbol, language, { speed: 0.9 })}
          className="flex h-7 w-7 items-center justify-center rounded-full text-fg-muted transition hover:bg-primary/10 hover:text-primary"
          aria-label={`Hear /${tile.symbol}/`}
        >
          <Volume2 size={15} strokeWidth={1.8} />
        </button>
      </div>

      <div className="min-w-0">
        {tile.correct === false ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-lg leading-7 text-accent">
              {tile.said || "—"}
            </span>
            {!tile.said && <span className="text-xs text-fg-muted">missing</span>}
            {tile.risk && (
              <span
                className={cn(
                  "h-2 w-2 rounded-full",
                  tile.risk.priority === "critical" ? "bg-danger" : "bg-accent"
                )}
                title="This slip can change the word's meaning"
              />
            )}
            <button
              type="button"
              onClick={toggleHint}
              className="inline-flex items-center gap-1 rounded-full border border-line bg-elevated px-2.5 py-0.5 text-xs font-medium text-fg transition hover:border-primary/50"
            >
              {hintOpen ? "Hide hint" : "Show hint"}
              {hintOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
          </div>
        ) : tile.correct === true ? (
          <span className="text-sm font-medium leading-7 text-primary">Excellent!</span>
        ) : (
          <span className="text-sm leading-7 text-fg-subtle">—</span>
        )}
      </div>

      {hintOpen && (
        <div className="col-span-2 pl-1">
          {hintLoading ? (
            <p className="text-xs text-fg-muted">Getting a tip…</p>
          ) : hintError ? (
            <p className="text-xs text-danger">Couldn&apos;t load a hint right now.</p>
          ) : (
            <ol className="relative ml-2.5 space-y-2 border-l border-line pl-4">
              {(hint || []).map((step, index) => (
                <li key={index} className="relative text-sm leading-5 text-fg-muted">
                  <span className="absolute -left-[26px] top-0 flex h-5 w-5 items-center justify-center rounded-full bg-elevated font-mono text-[10px] text-fg">
                    {index + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
};

const WordCard = ({
  word,
  language,
  nativeLanguage,
  phonemes,
  phonemeMatches,
  transcribedPhonemes,
  accuracy,
  soundRisks,
}) => {
  const { soundEffects } = useStore().settings;
  const { setWordPracticeDialogOpened } = useStore().learningSessionConfig;
  const practice = usePhonemeRecognition();
  const languageBase = (language || "en").split("-")[0];
  const nativeBase = (nativeLanguage || "en").split("-")[0];

  const [translation, setTranslation] = useState("");
  const [trend, setTrend] = useState([]);
  const [practiced, setPracticed] = useState(false);

  // Keep the practice screen's keyboard shortcuts off while the card is open.
  useEffect(() => {
    setWordPracticeDialogOpened(true);
    return () => {
      setWordPracticeDialogOpened(false);
      practice.cancelPhonemeRecognition();
      stopSpeaking();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (languageBase === nativeBase || !word) return;
    TranslateService.translateText(word, languageBase, nativeBase)
      .then((result) => setTranslation(result?.translated_text || ""))
      .catch(() => setTranslation(""));
  }, [word, languageBase, nativeBase]);

  useEffect(() => {
    if (!word) return;
    PracticeSessionStatsService.getAccuracyTrendForWord(word, languageBase)
      .then((data) => setTrend(Array.isArray(data) ? data.slice(-8) : []))
      .catch(() => setTrend([]));
  }, [word, languageBase, practiced]);

  // A fresh attempt inside the card replaces the result from the main session.
  const hasPractice = practice.directMatches.length > 0 && !practice.isRecording;
  useEffect(() => {
    if (!hasPractice) return;
    setPracticed((count) => count + 1);
    if (soundEffects && Math.floor(practice.overallAccuracy) === 100 && successSound) {
      successSound.currentTime = 0;
      successSound.play().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practice.directMatches]);

  const tiles = useMemo(
    () =>
      hasPractice
        ? tilesFromMatches(practice.directMatches)
        : tilesFromSegment(phonemeMatches, transcribedPhonemes, phonemes, soundRisks),
    [hasPractice, practice.directMatches, phonemeMatches, transcribedPhonemes, phonemes, soundRisks]
  );
  const score = hasPractice
    ? practice.overallAccuracy
    : typeof accuracy === "number"
    ? accuracy
    : null;
  const ipa = tiles.map((tile) => tile.symbol).join("");
  const graded = tiles.some((tile) => tile.correct !== null);
  // Functional load: the most serious slip that could turn this into another word.
  const worstRisk = tiles
    .map((tile) => tile.risk)
    .filter(Boolean)
    .sort((a, b) => (b.score || 0) - (a.score || 0))[0];

  const isRecording = practice.isRecording;
  const isChecking = practice.isPredicting;

  const togglePractice = () => {
    if (isRecording) {
      practice.stopPhonemeRecognition();
    } else {
      stopSpeaking();
      practice.startPhonemeRecognition(word, languageBase, false);
    }
  };

  const listen = (speed) => speakText(word, language, { speed });

  return (
    <div className="flex flex-col gap-4">
      {/* Word, pronunciation and score */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-display text-2xl font-bold tracking-wide text-fg">
            {word}
          </div>
          {ipa && (
            <div className="mt-0.5 font-mono text-sm text-fg-muted">
              /
              {tiles.map((tile, index) => (
                <span
                  key={`${tile.symbol}-${index}`}
                  className={cn(
                    tile.correct === true && "text-primary",
                    tile.correct === false && "text-accent"
                  )}
                >
                  {tile.symbol}
                </span>
              ))}
              /
            </div>
          )}
        </div>
        {score !== null && graded && <ScoreBadge value={score} />}
      </div>

      {worstRisk?.sounds_like && (
        <div className="-mt-2 flex items-center gap-2 text-sm text-fg-muted">
          <PriorityBadge priority={worstRisk.priority} />
          <span>
            Could sound like <span className="font-semibold text-fg">“{worstRisk.sounds_like}”</span>
          </span>
        </div>
      )}

      {/* Listen + practice */}
      <div className="grid grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => listen(1)}
          className="flex flex-col items-center gap-1 rounded-lg border border-line bg-surface py-2.5 text-xs font-medium text-fg-muted transition hover:border-primary/50 hover:text-primary"
        >
          <Volume2 size={18} strokeWidth={1.8} />
          Listen
        </button>
        <button
          type="button"
          onClick={() => listen(0.6)}
          className="flex flex-col items-center gap-1 rounded-lg border border-line bg-surface py-2.5 text-xs font-medium text-fg-muted transition hover:border-primary/50 hover:text-primary"
        >
          <Turtle size={18} strokeWidth={1.8} />
          Slow
        </button>
        <button
          type="button"
          onClick={togglePractice}
          disabled={isChecking}
          className={cn(
            "flex flex-col items-center gap-1 rounded-lg border py-2.5 text-xs font-semibold transition disabled:opacity-60",
            isRecording
              ? "animate-pulse border-accent bg-accent/15 text-accent"
              : "border-primary/60 bg-primary/10 text-primary hover:bg-primary/20"
          )}
        >
          {isChecking ? (
            <span className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
          ) : isRecording ? (
            <Square size={18} strokeWidth={1.8} />
          ) : (
            <Mic size={18} strokeWidth={1.8} />
          )}
          {isChecking ? "Checking" : isRecording ? "Stop" : "Say it"}
        </button>
      </div>

      {/* Sound by sound */}
      {tiles.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-line">
          <div className="grid grid-cols-[88px_1fr] border-b border-line bg-surface px-3 py-2">
            <span className="hud-label">Sound</span>
            <span className="hud-label">You said</span>
          </div>
          <div className="max-h-[40vh] divide-y divide-line overflow-y-auto">
            {tiles.map((tile, index) => (
              <SoundRow
                key={`${tile.symbol}-${index}`}
                tile={tile}
                language={language}
                nativeLanguage={nativeBase}
              />
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-fg-muted">
          Tap <span className="font-semibold text-primary">Say it</span> to see how
          each sound comes out.
        </p>
      )}

      {/* Meaning + progress */}
      {(translation || trend.length > 1) && (
        <div className="flex items-center justify-between gap-3 border-t border-line pt-3 text-sm">
          {translation ? (
            <span className="flex min-w-0 items-center gap-2 text-fg-muted">
              <Languages size={14} className="shrink-0 text-primary" />
              <span className="truncate">{he.decode(translation)}</span>
            </span>
          ) : (
            <span />
          )}
          {trend.length > 1 && (
            <span className="flex shrink-0 items-end gap-0.5" title="Your recent scores for this word">
              {trend.map((point, index) => (
                <span
                  key={index}
                  className="w-1.5 rounded-sm"
                  style={{
                    height: `${Math.max(4, (point.accuracy / 100) * 20)}px`,
                    backgroundColor: accuracyToColor(point.accuracy),
                  }}
                />
              ))}
            </span>
          )}
        </div>
      )}

      {practice.error && (
        <p className="text-xs text-danger">Couldn&apos;t check that one — {practice.error}</p>
      )}
    </div>
  );
};

export default WordCard;
